<!-- public-schedule component -->
<div class="container-fluid py-4">
  <!-- Header -->
  <div class="card bg-linear-gradient-purple text-white mb-4 p-3 p-md-4">
    <div
      class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center text-center text-sm-start"
    >
      <div class="mb-3 mb-sm-0">
        <h2 class="mb-1 fs-5 fs-sm-4">
          <i class="bi bi-calendar3 me-2"></i>Published Schedules
        </h2>
        <p class="mb-0 opacity-75 small">
          View current and previous weekly work schedules
        </p>
      </div>

      <!-- View Today & Tomorrow Buttons -->
      <div class="d-flex gap-2 mt-2 mt-sm-0">
        <button
          class="btn btn-light btn-sm px-3 py-2 shadow-sm"
          style="font-size: 0.8rem"
          (click)="openTodayScheduleModal()"
          [disabled]="!getCurrentWeekSchedule()"
        >
          <i class="bi bi-clock me-1"></i> View Today
        </button>

        <button
          class="btn btn-light btn-sm px-3 py-2 shadow-sm"
          style="font-size: 0.8rem"
          (click)="openTomorrowScheduleModal()"
          [disabled]="!getTomorrowWeekSchedule()"
        >
          <i class="bi bi-clock-history me-1"></i> View Tomorrow
        </button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="text-center py-5">
    <div
      class="spinner-border text-primary"
      role="status"
      style="width: 3rem; height: 3rem"
    >
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-3 text-muted">Loading schedules...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !isLoading" class="alert alert-danger" role="alert">
    <i class="bi bi-exclamation-triangle me-2"></i>
    {{ error }}
  </div>

  <!-- No Schedules -->
  <div
    *ngIf="!isLoading && !error && publishedSchedules.length === 0"
    class="text-center py-5"
  >
    <i class="bi bi-calendar-x display-1 text-muted"></i>
    <h4 class="mt-3 text-muted">No Published Schedules</h4>
    <p class="text-muted">Check back later for your work schedule</p>
  </div>

  <!-- Schedules List -->
  <div *ngIf="!isLoading && !error && publishedSchedules.length > 0">
    <div class="row">
      <div
        *ngFor="let schedule of paginatedSchedules"
        class="col-md-6 col-lg-4 mb-4"
      >
        <div
          class="card h-100 shadow-sm hover-card"
          [class.border-gradient]="isCurrentWeek(schedule)"
          [class.border-3]="isCurrentWeek(schedule)"
        >
          <div
            class="card-header"
            [class.bg-linear-gradient-purple]="isCurrentWeek(schedule)"
            [class.text-white]="isCurrentWeek(schedule)"
            [class.bg-light]="!isCurrentWeek(schedule)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="bi bi-calendar-week me-2"></i>
                {{ formatWeekLabel(schedule) }}
              </h5>
            </div>
          </div>

          <div class="card-body">
            <div class="mb-3">
              <small class="text-muted d-block">
                <i class="bi bi-clock-history me-1"></i>
                Published {{ schedule.publishedAt?.toDate() | date : "short" }}
              </small>
            </div>

            <div class="d-flex gap-2 mb-3">
              <div class="flex-fill text-center p-2 bg-light rounded">
                <div class="fs-5 fw-bold text-primary">
                  {{ schedule.result.length || 0 }}
                </div>
                <small class="text-muted">Employees</small>
              </div>
            </div>

            <button
              class="btn btn-gradient w-100"
              (click)="openScheduleModal(schedule)"
            >
              <i class="bi bi-eye me-2"></i>View Schedule
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ Pagination Controls -->
    <nav aria-label="Schedule pagination" *ngIf="totalPages > 1">
      <ul class="pagination justify-content-center">
        <!-- Previous Button -->
        <li class="page-item" [class.disabled]="currentPage === 1">
          <a class="page-link" (click)="previousPage()" style="cursor: pointer">
            <i class="bi bi-chevron-left"></i> Back
          </a>
        </li>

        <!-- Page Numbers -->
        <li
          *ngFor="let page of pageNumbers"
          class="page-item"
          [class.active]="currentPage === page"
        >
          <a class="page-link" (click)="goToPage(page)" style="cursor: pointer">
            {{ page }}
          </a>
        </li>

        <!-- Next Button -->
        <li class="page-item" [class.disabled]="currentPage === totalPages">
          <a class="page-link" (click)="nextPage()" style="cursor: pointer">
            Next <i class="bi bi-chevron-right"></i>
          </a>
        </li>
      </ul>
    </nav>
    <div
      class="d-flex justify-content-end align-items-end mb-3"
      *ngIf="publishedSchedules.length > 3"
    >
      <p class="text-muted mb-0">
        <i class="bi bi-calendar-check me-1"></i>
        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} -
        {{ Math.min(currentPage * itemsPerPage, publishedSchedules.length) }}
        of {{ publishedSchedules.length }} schedules
      </p>
    </div>
  </div>
</div>

<!-- View Schedule Modal -->
<div
  class="modal fade"
  id="viewScheduleModal"
  tabindex="-1"
  aria-labelledby="viewScheduleModalLabel"
  aria-hidden="true"
>
  <div
    class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
  >
    <div class="modal-content">
      <div class="modal-header bg-linear-gradient-purple text-white">
        <h5 class="modal-title" id="viewScheduleModalLabel">
          <i class="bi bi-calendar-week me-2"></i>
          <span
            *ngIf="selectedSchedule && !isViewingToday && !isViewingTomorrow"
          >
            Week of {{ formatWeekLabel(selectedSchedule) }}
          </span>
          <span *ngIf="selectedSchedule && isViewingToday">
            {{ todayLabel }}'s Schedule
          </span>
          <span *ngIf="selectedSchedule && isViewingTomorrow">
            {{ tomorrowLabel }}'s Schedule
          </span>
        </h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>

      <div class="modal-body">
        <!-- Today View -->
        <div *ngIf="isViewingToday && selectedSchedule" class="py-2">
          <div class="text-center mb-3">
            <h2 class="text-muted">{{ todayLabel }}</h2>
            <h3 class="fw-semibold mb-0">
              <i class="bi bi-person-badge me-1"></i>{{ employeeName }}
            </h3>
          </div>

          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body text-center">
              <h6 class="text-secondary mb-1">Your Shift</h6>
              <h5
                [ngClass]="{
                  'text-success':
                    todayShift?.toLowerCase() !== 'off today' &&
                    todayShift?.toLowerCase() !== 'requested off',
                  'text-danger': todayShift?.toLowerCase() === 'requested off',
                  'text-muted': todayShift?.toLowerCase() === 'off today'
                }"
                class="fw-bold"
              >
                {{ todayShift }}
              </h5>
            </div>
          </div>

          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body">
              <h6 class="fw-semibold mb-2 text-secondary">
                <i class="bi bi-people-fill me-1"></i>Also Working Today
              </h6>

              <div *ngIf="coworkersToday.length > 0; else noCoworkers">
                <ul class="list-group list-group-flush small">
                  <li
                    *ngFor="let coworker of coworkersToday"
                    class="list-group-item px-0 py-1 border-0 d-flex align-items-center"
                  >
                    <i class="bi bi-person me-1 text-primary"></i>
                    <span>{{ coworker }}</span>
                  </li>
                </ul>
              </div>

              <ng-template #noCoworkers>
                <p class="text-muted small mb-0">
                  No other coworkers scheduled today.
                </p>
              </ng-template>
            </div>
          </div>

          <div class="text-center mt-2">
            <small class="text-muted">
              <i class="bi bi-clock-history me-1"></i>
              Published
              {{ selectedSchedule.publishedAt?.toDate() | date : "short" }}
            </small>
          </div>
        </div>

        <!-- Tomorrow View -->
        <div *ngIf="isViewingTomorrow && selectedSchedule" class="py-2">
          <div class="text-center mb-3">
            <h2 class="text-muted">{{ tomorrowLabel }}</h2>
            <h3 class="fw-semibold mb-0">
              <i class="bi bi-person-badge me-1"></i>{{ employeeName }}
            </h3>
          </div>

          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body text-center">
              <h6 class="text-secondary mb-1">Your Shift</h6>
              <h5
                [ngClass]="{
                  'text-success':
                    tomorrowShift?.toLowerCase() !== 'off tomorrow' &&
                    tomorrowShift?.toLowerCase() !== 'requested off',
                  'text-danger':
                    tomorrowShift?.toLowerCase() === 'requested off',
                  'text-muted': tomorrowShift?.toLowerCase() === 'off tomorrow'
                }"
                class="fw-bold"
              >
                {{ tomorrowShift }}
              </h5>
            </div>
          </div>

          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body">
              <h6 class="fw-semibold mb-2 text-secondary">
                <i class="bi bi-people-fill me-1"></i>Also Working Tomorrow
              </h6>

              <div
                *ngIf="coworkersTomorrow.length > 0; else noCoworkersTomorrow"
              >
                <ul class="list-group list-group-flush small">
                  <li
                    *ngFor="let coworker of coworkersTomorrow"
                    class="list-group-item px-0 py-1 border-0 d-flex align-items-center"
                  >
                    <i class="bi bi-person me-1 text-primary"></i>
                    <span>{{ coworker }}</span>
                  </li>
                </ul>
              </div>

              <ng-template #noCoworkersTomorrow>
                <p class="text-muted small mb-0">
                  No other coworkers scheduled tomorrow.
                </p>
              </ng-template>
            </div>
          </div>

          <div class="text-center mt-2">
            <small class="text-muted">
              <i class="bi bi-clock-history me-1"></i>
              Published
              {{ selectedSchedule.publishedAt?.toDate() | date : "short" }}
            </small>
          </div>
        </div>

        <!-- Full Week View -->
        <div *ngIf="!isViewingToday && !isViewingTomorrow && selectedSchedule">
          <app-view-published-schedule
            [schedule]="selectedSchedule"
          ></app-view-published-schedule>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
          Close
        </button>
      </div>
    </div>
  </div>
</div>
